import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { trackPageView, trackEvent } from '../utils/TrackPageViews';

/**
 * Component that tracks page views with additional data
 * Useful for pages that need custom tracking data
 */
const PageTracker = ({ 
  children, 
  pageTitle, 
  pageType, 
  pageData = {},
  skipTracking = false,
  trackImpressionEvent = false
}) => {
  useEffect(() => {
    if (typeof window === 'undefined' || skipTracking) return;
    
    try {
      // Track the page view
      trackPageView({
        title: pageTitle || document.title,
        path: window.location.pathname,
        ...pageData,
        pageType
      });
      
      // Track page impression event if requested
      // This is useful for product and category pages where impressions matter for analytics
      if (trackImpressionEvent) {
        trackEvent('page_impression', {
          pageType,
          title: pageTitle || document.title,
          ...pageData
        });
      }
      
      // Send a custom event for monitoring
      if (typeof window.CustomEvent === 'function') {
        const event = new CustomEvent('app:page_loaded', {
          detail: {
            pageType,
            title: pageTitle || document.title,
            timestamp: new Date().toISOString()
          },
          bubbles: true
        });
        document.dispatchEvent(event);
      }
    } catch (error) {
      console.error('Error in PageTracker component:', error);
    }
  }, [pageTitle, pageType, pageData, skipTracking, trackImpressionEvent]);

  return <>{children}</>;
};

PageTracker.propTypes = {
  children: PropTypes.node.isRequired,
  pageTitle: PropTypes.string,
  pageType: PropTypes.string,
  pageData: PropTypes.object,
  skipTracking: PropTypes.bool,
  trackImpressionEvent: PropTypes.bool
};

export default PageTracker;